/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getChatBySessionId,
  imageAndVideoModels,
  // imageAndVideoModels, // Uncomment when ready to use API
  setPage,
  setSelectedChat,
  setSelectedProjectChat,
  setUser,
} from "../../lib/redux/actions";
import DaburDropDown from "./DaburDropDown";
import Sidebar from "./Sidebar";
import LogoutDropDown from "./LogoutDropDown";
import { useMsal } from "@azure/msal-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { DisclaimerDialog } from "./DisclaimerDialog";
import { trackEvent, trackException } from "../../lib/utils/telemetryService";
import copilotIcon from "../../assets/svg/copilot.svg";
import fallbackModels from "../../assets/data/AIModels.json"


export default function MainContainer() {
  const { instance } = useMsal();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [chats, setChats] = useState([]);
  const [projectChats,setProjectChats] = useState([])
  const [marketingChats,setMarketingChats] = useState([])
  const [meetingTranscriptionChats, setMeetingTranscriptionChats] = useState([]);
  const [recentChatsData, setRecentChatsData] = useState({
    today: [],
    last7Days: [],
    last30Days: [],
    older: [],
  });
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedChat, selectedProjectChat } = useSelector((state) => state.chat);
  const location = useLocation();

  // Model selector state (for GenerateImage and GenerateVideo pages)
  const [modelSelectorConfig, setModelSelectorConfig] = useState(null);
  const [imageModels, setImageModels] = useState([]);
  const [videoModels, setVideoModels] = useState([]);
  const [chatModels, setChatModels] = useState([]);
  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts?.length > 0) {
      const userDataFromToken = {
        username: accounts[0]?.username,
        name: accounts[0]?.name,
        email: accounts[0]?.username,
        idToken: accounts[0]?.idToken,
      };

      dispatch(setUser(userDataFromToken));
      // localStorage.clear()
    }
  }, [dispatch, instance]);

  const fetchRecentChats = async () => {
    try {
      setLoading(true);
      const data = await getChatBySessionId();

      if (data.status === 1) {
        setShowDisclaimer(!data.data.isDisclaimerAccepted);

        setRecentChatsData({
          today: data?.data?.today,
          last7Days: data?.data?.last7Days,
          last30Days: data?.data?.last30Days,
          older: data?.data?.older,
          projectsListing: data?.data?.projects,
        });
        trackEvent("FetchChatsSessions_Success", {
          totalToday: data?.data?.today?.length,
          total7Days: data?.data?.last7Days?.length,
          total30Days: data?.data?.last30Days?.length,
          totalOlder: data?.data?.older?.length,
        });
      } else {
        setError(data.message || "Failed to fetch chats");
        trackEvent("FetchRecentSessions_Failed", { message: data.message });
      }
    } catch (err) {
      setError(err.message || "Network error");
      trackException(err);
    } finally {
      setLoading(false);
    }
  };
  const { modelsLoaded ,modelsList} = useSelector(state => state.chat);
// const dispatch = useDispatch();

const fetchModelsList = async () => {
  try {
    const res = await imageAndVideoModels();
    const apiData = res?.data?.data;

    handleModelsData(apiData);

    dispatch({
      type: "SET_MODELS_LIST",
      payload: apiData,
    });

  } catch (err) {
    trackException(err);

    trackEvent("FetchImageVideoModels_Failed", {
      message: err?.message || "Failed to fetch image and video models",
    });

    // fallback
    handleModelsData(fallbackModels?.data);

    dispatch({
      type: "SET_MODELS_LIST",
      payload: fallbackModels?.data,
    });
  }
};

  const handleModelsData = (data) => {
    // Ensure data is a valid array
    if (!Array.isArray(data) || data.length === 0) {
      data = fallbackModels?.data || [];
    }

    const imageModelsData = data.filter(item => item?.type === "IMAGE");
    const videoModelsData = data.filter(item => item?.type === "VIDEO");
    const chatModelsData = data.filter(item => item?.type === "TEXT");

    setImageModels(imageModelsData);
    setVideoModels(videoModelsData);
    setChatModels(chatModelsData);
  };


  useEffect(() => {
    fetchRecentChats();
    // Use redux data immediately if already loaded for fast rendering
    if (modelsLoaded && modelsList?.length > 0) {
      handleModelsData(modelsList);
    } 
    
    // Always fetch latest models in the background to ensure data is up to date
    fetchModelsList();
  }, []);

  const router = useNavigate();

  const handleSelectChat = (id, chat) => {
    if (selectedChat?._id === id) return;
    setChats([]);
    dispatch(setPage(1));
    dispatch(setSelectedChat(chat));
    router(`/chat/${chat._id}`);
    trackEvent("ChatSessionSelected", { chatId: chat._id });
  };

  const handleSelectProjectChat = (id,chat)=>{
    if (selectedProjectChat?._id === id) return;

    setProjectChats([]);
    setMarketingChats([])
    dispatch(setPage(1));
    dispatch(setSelectedProjectChat(chat));
    router(`/project/${chat.name}/${chat.projectId}/${chat._id}`);
    // trackEvent("ChatSessionSelected", { chatId: chat._id });
  }

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  const handleCopilotRedirect = () => {
    window.open("https://copilot.microsoft.com", "_blank");
  };

  // Clear model selector when navigating away from pages that use it
  useEffect(() => {
    const isModelSelectorPage =
      location.pathname === "/generate-image" ||
      location.pathname === "/generate-video" ||
      location.pathname === "/" ||
      location.pathname.startsWith("/chat/")
    if (!isModelSelectorPage && modelSelectorConfig !== null) {
      setModelSelectorConfig(null);
    }
  }, [location.pathname, modelSelectorConfig]);

  return (
    <>
      <DisclaimerDialog
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
      />
      <div className="loader">
        <div className="loader__content"></div>
      </div>
      <div className={"flex h-[calc(100vh-0px)]"}>
        <div className="fixed top-0 left-[2%] right-0 h-16 bg-white border-b border-gray-200 z-20 flex items-center justify-between px-2 sm:px-4 overflow-visible">
          {isMobile && !sidebarOpen && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="flex items-center justify-center"
              aria-label="Open sidebar menu"
            >
              <Menu className="text-[#1B5C29]" />
            </button>
          )}
          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
            <DaburDropDown
              sidebarOpen={sidebarOpen}
              isMobile={isMobile}
              modelSelectorConfig={modelSelectorConfig}
            />


          </div>

          <div className="flex flex-row items-center justify-center gap-2 sm:gap-[20px] mr-2 sm:mr-4 flex-shrink-0">
            <button
              onClick={handleCopilotRedirect}
              className="flex items-center justify-center gap-1 sm:gap-2"
            >
              <div className="hidden sm:flex flex-col cursor-pointer items-center justify-center">
                <h3 className="text-[14px]  text-gray-500">
                  Switch to Dabur Copilot
                </h3>
                <h5 className="text-[12px] leading-[100%] text-gray-500 italic">
                  Powered by Microsoft
                </h5>
              </div>

              <img
                src={copilotIcon}
                alt="Copilot"
                className="w-[18px] h-[16px] sm:w-[22px] sm:h-[20px] block"
              />
            </button>

            <LogoutDropDown />
          </div>
        </div>
        {isMobile && sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black bg-opacity-50 z-30 appearance-none border-none p-0 m-0"
            aria-label="Close sidebar"
          />
        )}

        <Sidebar
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
          setPage={setPage}
          loading={loading}
          error={error}
          setChats={setChats}
          handleSelectChat={handleSelectChat}
          recentChatsData={recentChatsData}
          selectedChat={selectedChat}
          handleSelectProjectChat={handleSelectProjectChat}
          closeSidebar={() => setSidebarOpen(false)}
          setProjectChats={setProjectChats}
          fetchRecentChats={fetchRecentChats}
        />
        <Outlet
          context={{
            isMobile,
            sidebarOpen,
            setChats,
            chats,
            fetchRecentChats,
            setModelSelectorConfig,
            imageModels,
            videoModels,
            chatModels,
            setMeetingTranscriptionChats,
            meetingTranscriptionChats,
            setProjectChats,
            projectChats,
            marketingChats,
            setMarketingChats
          }}
        />
      </div>
    </>
  );
}
